import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SectorBadge } from '@/components/ui/sector-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Download } from 'lucide-react';
import { format } from 'date-fns';

interface DiasporaProfessional {
  id: string;
  sectors: string[] | null;
  skills: string[] | null;
  open_to: string[] | null;
  years_experience: number | null;
  created_at: string;
  profiles: {
    first_name: string;
    last_name: string;
    email: string;
    country: string | null;
  };
}

export function DiasporaTab() {
  const { t } = useTranslation();
  const [professionals, setProfessionals] = useState<DiasporaProfessional[]>([]);
  const [loading, setLoading] = useState(true);
  const [sectorFilter, setSectorFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');

  useEffect(() => { fetchDiaspora(); }, []);

  const fetchDiaspora = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('professionals')
      .select('id, sectors, skills, open_to, years_experience, created_at, profiles!inner(first_name, last_name, email, country)')
      .eq('profiles.is_diaspora', true)
      .order('created_at', { ascending: false });

    setProfessionals((data as unknown as DiasporaProfessional[]) || []);
    setLoading(false);
  };

  const countries = useMemo(() => {
    const set = new Set<string>();
    professionals.forEach(p => {
      if (p.profiles?.country) set.add(p.profiles.country);
    });
    return Array.from(set).sort();
  }, [professionals]);

  const filtered = useMemo(() => {
    return professionals.filter(p => {
      if (sectorFilter !== 'all') {
        const sectors = (p.sectors as string[]) || [];
        if (!sectors.includes(sectorFilter)) return false;
      }
      if (countryFilter !== 'all') {
        if (p.profiles?.country !== countryFilter) return false;
      }
      return true;
    });
  }, [professionals, sectorFilter, countryFilter]);

  const exportCsv = () => {
    const headers = ['first_name','last_name','email','country','sectors','skills','open_to','years_experience','registered'];
    const rows = filtered.map(p => [
      p.profiles.first_name,
      p.profiles.last_name,
      p.profiles.email ?? '',
      p.profiles.country ?? '',
      ((p.sectors as string[]) ?? []).join(';'),
      ((p.skills as string[]) ?? []).join(';'),
      ((p.open_to as string[]) ?? []).join(';'),
      p.years_experience ?? '',
      new Date(p.created_at).toLocaleDateString()
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `diaspora-professionals-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('admin.tabs.diaspora')}</CardTitle>
        <Button onClick={exportCsv} variant="outline" size="sm" disabled={filtered.length === 0}>
          <Download className="mr-1 h-4 w-4" />{t('admin.exportCsv')}
        </Button>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="mb-4 flex flex-wrap gap-3">
          <Select value={sectorFilter} onValueChange={setSectorFilter}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Sector" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sectors</SelectItem>
              {['mining','processing','infrastructure','industrial','services','other'].map(s => (
                <SelectItem key={s} value={s}>{t(`sectors.${s}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Country" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Countries</SelectItem>
              {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center">No diaspora professionals found.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Sectors</TableHead>
                <TableHead>Skills</TableHead>
                <TableHead>Open To</TableHead>
                <TableHead>Registered</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(p => {
                const sectors = (p.sectors as string[]) || [];
                const skills = (p.skills as string[]) || [];
                const openTo = (p.open_to as string[]) || [];
                return (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.profiles.first_name} {p.profiles.last_name}</TableCell>
                    <TableCell>{p.profiles.country || '—'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {sectors.slice(0, 2).map(s => <SectorBadge key={s} sector={s as 'mining' | 'processing' | 'infrastructure' | 'industrial' | 'services' | 'other'} />)}
                        {sectors.length > 2 && <Badge variant="secondary" className="text-xs">+{sectors.length - 2}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {skills.slice(0, 2).map(s => <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>)}
                        {skills.length > 2 && <Badge variant="secondary" className="text-xs">+{skills.length - 2}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {openTo.map(o => o.charAt(0).toUpperCase() + o.slice(1)).join(', ') || '—'}
                    </TableCell>
                    <TableCell>{format(new Date(p.created_at), 'MMM d, yyyy')}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
